/**
 * What the composer is doing. **Pure**, so every state is checkable without a DOM.
 *
 * ⛔ THIS IS WHERE §9.0 RULE 4 FINALLY HAS A CONSUMER. `renderMessageError` and
 * `blocksComposer` were written two days ago and `blocksComposer` had **no
 * caller at all** - the rule-with-no-consumer shape at two days, caught by
 * looking rather than by anything failing.
 */
import { renderMessageError, type MessageError } from './message-error.js'

export type ComposerState =
  /**
   * ⛔ A FIFTH EMPTY STATE IN THIS COMPONENT TREE, and it is not "disabled".
   * Nothing is open, so there is nothing to compose INTO - which is a different
   * sentence from "you cannot send here", and an agent staring at a greyed box
   * would read the second.
   */
  | { kind: 'noConversation' }
  /**
   * ⛔ A SEND IS IN FLIGHT. **NOT AN ERROR, AND IT MUST NOT LOOK LIKE ONE.**
   *
   * This is the trap in the whole component: reusing the refusal styling here
   * tells an agent something is wrong EVERY TIME NOTHING IS. It is the
   * disconnected banner's argument - a signal people learn to ignore is worse
   * than no signal - except that this one fires on the HAPPY PATH, which is
   * worse: the banner at least only cried wolf when the socket flapped.
   */
  | { kind: 'sending' }
  /**
   * ⛔ BYTES ARE GOING TO OUR SERVER. A FOURTH ARM, NOT A REUSE OF `sending`.
   *
   * `sending` means **the row is committed and going to Meta**. An upload has
   * committed nothing: no row exists yet, and a failure here loses a file rather
   * than a message. Wearing `sending` would make a failed upload read as a
   * failed send - a customer not receiving something, rather than a file not
   * arriving - which is a different thing to tell somebody and a different
   * recovery.
   *
   * ⚠️ AND IT SHARES NO CLASS WITH `blocked`, for the reason already written
   * above `sending`: this fires on the happy path, every time.
   *
   * ⛔ THE FILE STAYS SELECTED ON FAILURE. The text path restores the draft
   * because retyping is unreproducible work; here the argument is stronger -
   * re-selecting is a FILE-PICKER ROUND TRIP, not retyping, and an agent who
   * dragged a photo in has nothing to drag again if the component forgets it.
   * Retry re-uploads; it does not ask them to choose again.
   */
  | { kind: 'uploading' }
  /** A condition prevents sending. The reason is rule 4's, rendered verbatim. */
  | { kind: 'blocked'; reason: MessageError }
  | { kind: 'ready' }

export type ComposerInputs = {
  conversationId: string | null
  sending: boolean
  /** Bytes are on their way to OUR server. See the `uploading` state. */
  uploading?: boolean
  /**
   * The newest outbound message's `error_code`, or null.
   *
   * ⚠️ **A PROXY FOR A CONDITION, AND THE ONLY ONE AVAILABLE TODAY.** The window
   * state is not on the wire: the thread endpoint returns messages and nothing
   * tells the client the window is closed BEFORE a send. So the composer is
   * REACTIVE - it learns from a failure - and a proactive read is a
   * thread-endpoint change that is not in this piece.
   */
  lastErrorCode: string | null
}

/**
 * ┌─ ⛔ WHICH CODES BLOCK, AND WHY THIS IS NOT THE REFINEMENT RULE 4 DEFERRED ──┐
 * │ Rule 4 decided: *"COMPOSER DISABLE IS UNIFORM ACROSS ALL REFUSAL CODES, not │
 * │ per-code. Per-code is a refinement that wants evidence from real use."*     │
 * │ That settled WHICH message each code shows. **It did not settle for how     │
 * │ long the composer stays disabled**, and that question cannot be deferred    │
 * │ because the naive answer is unusable:                                       │
 * │                                                                            │
 * │ ⛔ THE COMPOSER'S ONLY SIGNAL IS THE NEWEST OUTBOUND MESSAGE. If a           │
 * │ transport blip disables it, the agent cannot send - so no newer message     │
 * │ exists - so the signal never changes. **A one-second network failure would  │
 * │ disable the composer permanently.** That is a deadlock, not a policy.       │
 * │                                                                            │
 * │ ✅ SO THE SPLIT IS BY WHETHER RETRYING HELPS, AND IT IS DERIVED RATHER THAN │
 * │ INVENTED: `renderMessageError` already decides that, as `retry.kind`.       │
 * │                                                                            │
 * │   retry: 'retry'     transient. Sending again IS the action -> READY        │
 * │   retry: 'template'  the window is shut -> BLOCKED, and only a customer      │
 * │                      message reopens it                                     │
 * │   retry: 'none'      a condition nobody here can clear -> BLOCKED            │
 * │                                                                            │
 * │ ⚠️ THIS IS A SECOND CONSUMER OF ONE DECISION, NOT A SECOND COPY OF IT. If   │
 * │ a code's retryability changes, both the bubble and the composer follow -    │
 * │ which is the `scopeFilter`-returning-a-fragment argument in another place.  │
 * └────────────────────────────────────────────────────────────────────────────┘
 */
export function composerState(inputs: ComposerInputs): ComposerState {
  // ⛔ ORDER IS THE GUARANTEE. Nothing open outranks everything; an in-flight
  // send outranks a stale failure, or the agent watches a refusal while their
  // message is on its way.
  if (inputs.conversationId === null) return { kind: 'noConversation' }
  /**
   * ⚠️ UPLOADING OUTRANKS SENDING, and the two cannot be true at once anyway:
   * the row does not exist until the upload finishes, so nothing can be in
   * flight to Meta while bytes are still arriving here. Ordered explicitly
   * rather than left to that invariant, because the invariant is a property of
   * today's route and this is a claim about what the agent is told.
   */
  if (inputs.uploading) return { kind: 'uploading' }
  if (inputs.sending) return { kind: 'sending' }

  const failure = renderMessageError(inputs.lastErrorCode)
  if (failure === null) return { kind: 'ready' }
  if (failure.retry.kind === 'retry') return { kind: 'ready' }
  return { kind: 'blocked', reason: failure }
}

/**
 * May the agent type? ⚠️ SEPARATE FROM "may they send", because a blocked
 * composer that also refuses KEYSTROKES loses whatever they had written - and
 * the thing they wrote is the only part of this a person cannot reproduce.
 */
export function canType(state: ComposerState): boolean {
  return state.kind !== 'noConversation'
}

/** May a commit be attempted? */
export function canSend(state: ComposerState): boolean {
  return state.kind === 'ready'
}
